import { vatFormats } from '../../../../common/StringUtils';
import type { CustomerFormData, ValidationErrors } from '../types';

/**
 * Validates email format using regex pattern
 */
export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
};

/**
 * Validates a comma-separated list of email addresses.
 * Returns true only if every non-empty entry is a valid email.
 */
export const validateEmailList = (emails: string): boolean => {
    const parts = emails
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0);
    if (parts.length === 0) return false;
    return parts.every((e) => validateEmail(e));
};

/**
 * Validates a customer / business name.
 * Allows alphabetic characters (including accented and international letters),
 * numeric characters (anywhere), spaces and the characters comma (,),
 * hyphen (-), apostrophe ('), ampersand (&) and slash (/).
 * Any other special character ( <>?.":{}[]|\!@#$%^*()~` ) makes it invalid.
 */
export const validateCustomerName = (name: string): boolean => {
    const allowedPattern = /^[\p{L}\p{N}\s,'&\/-]+$/u;
    return allowedPattern.test(name);
};
/**
 * Validates VAT number format based on country code
 */ 
export const validateVatNumber = (
    vatCountryCode: string, 
    vatNumber: string,
    setVatNumberError: (error: string) => void,
    t?: (key: string, options?: any) => string
): boolean => {
    const format = (vatFormats as any)[vatCountryCode];
    if (!format) {
        setVatNumberError('');
        return true;
    }

    const exampleFormat = (vatFormats as any)[vatCountryCode]?.exampleFormat || '';
    const { regex, minLength, maxLength } = format;
   
    // Strip the country code from the VAT number before validation
    let vatNumberToValidate = vatNumber;
    if (vatNumber && vatNumber.toUpperCase().startsWith(vatCountryCode.toUpperCase())) { 
        vatNumberToValidate = vatNumber.slice(vatCountryCode.length);
    }
   
    if (!regex.test(vatNumberToValidate)) {
        let errorMessage = '';
        if (vatNumberToValidate?.length < minLength || vatNumberToValidate?.length > maxLength) {
            // If min and max are same, show example format instead of "between X and X digits"
            if (minLength === maxLength) {
                errorMessage = t 
                    ? t('INVALID_VAT_FORMAT_EXPECTED', { format: exampleFormat })
                    : `Invalid VAT number format. Expected format: ${exampleFormat}`;
            } else {
                errorMessage = t
                    ? t('INVALID_VAT_FORMAT_LENGTH', { min: minLength, max: maxLength })
                    : `Invalid VAT number format. It should be between ${minLength} and ${maxLength} digits long.`;
            }
        } else {
            errorMessage = t
                ? t('INVALID_VAT_FORMAT_EXPECTED', { format: exampleFormat })
                : `Invalid VAT number format. Expected format: ${exampleFormat}`;
        }
        setVatNumberError(errorMessage);
        return false;
    }

    setVatNumberError('');
    return true;
};

/**
 * Validates business information (Step 1)
 */
export const validateStep1 = (
    formData: CustomerFormData,
    validateVatNumberFn: (vatCountryCode: string, vatNumber: string) => boolean
): { errors: ValidationErrors; isValid: boolean } => {
    const errors: ValidationErrors = {};
    let isValid = true;

    if (!formData.businessType) {
        errors.businessType = 'Business type is required';
        isValid = false;
    }

    if (!formData.customerName) {
        errors.customerName = 'Customer name is required';
        isValid = false;
         } else if (!validateCustomerName(formData.customerName)) {
        errors.customerName = 'CUSTOMER_NAME_INVALID_CHARACTERS';
        isValid = false;
    
    }

    if (formData.businessType === '1') {
        // Individual account — the SSN field is ALWAYS required (both Sweden and
        // any other country). The Swedish personnummer FORMAT rules (exactly 12
        // digits, last 4 not all zeros) apply ONLY when the country is Sweden;
        // for other countries any (numeric) value is accepted.
        const isSweden = (formData.country || '').trim().toLowerCase() === 'sweden';
        if (!formData.ssn) {
            errors.ssn = 'SSN is required';
            isValid = false;
        } else if (isSweden) {
            if (formData.ssn.length !== 12) {
                errors.ssn = 'SSN must be exactly 12 digits';
                isValid = false;
            } else if (formData.ssn.slice(-4) === '0000') {
                errors.ssn = 'The last 4 digits of SSN cannot be all zeros.';
                isValid = false;
            }
        }
    } else {
        // Company account - validate organization number
        if (!formData.orgNo) {
            errors.orgNo = 'Organization number is required';
            isValid = false;
        }
    }

    if (!formData.country) {
        errors.country = 'Country is required';
        isValid = false;
    }

    if (!formData.addressLine1) {
        errors.addressLine1 = 'Address is required';
        isValid = false;
    }

    if (!formData.city) {
        errors.city = 'City is required';
        isValid = false;
    }

    if (!formData.zipCode) {
        errors.zipCode = 'ZIP code is required';
        isValid = false;
    }

    // VAT number validation for companies - only if isVATRegistered is 'true'
    if (formData.businessType !== '1' && formData.isVATRegistered === 'true') {
        if (!formData.vatNumber) {
            errors.vatNumber = 'VAT number is required';
            isValid = false;
        } else if (!validateVatNumberFn(formData.vatCountryCode, formData.vatNumber)) {
            isValid = false;
        }
    }

    return { errors, isValid };
};

/**
 * Validates contact information (Step 2)
 */
export const validateStep2 = (formData: CustomerFormData): { errors: ValidationErrors; isValid: boolean } => {
    const errors: ValidationErrors = {};
    let isValid = true;

    if (!formData.phoneNumber) {
        errors.phoneNumber = 'Phone number is required';
        isValid = false;
    }

    if (!formData.invoiceDeliveryMethod) {
        errors.invoiceDeliveryMethod = 'Invoice delivery method is required';
        isValid = false;
    }

    // Invoice language validation
    if (!formData.invoiceLanguage) {
        errors.invoiceLanguage = 'Invoice language is required';
        isValid = false;
    }

    // Email validation for non-postal delivery
    if (formData.invoiceDeliveryMethod !== '3' && !formData.contactEmail) {
        errors.contactEmail = 'Email is required';
        isValid = false;
    } else if (formData.contactEmail && !validateEmail(formData.contactEmail)) {
        errors.contactEmail = 'Invalid email address';
        isValid = false;
    }

    // CC email is optional and may contain multiple comma-separated emails,
    // but if provided every address must be valid
    if (formData.ccEmail && !validateEmailList(formData.ccEmail)) {
        errors.ccEmail = 'Invalid email address';
        isValid = false;
    }

    // E-invoice specific validations
    if (formData.invoiceDeliveryMethod === '2') {
        if (!formData.yourElectronicReference) {
            errors.yourElectronicReference = 'Your electronic reference is required';
            isValid = false;
        }

        if (!formData.globalLocationNumber) {
            errors.globalLocationNumber = 'GLN / Peppol ID is required';
            isValid = false;
        } else if (formData.globalLocationNumber.length < 13 || formData.globalLocationNumber.length > 18) {
            errors.globalLocationNumber = 'GLN / Peppol ID should be between 13 and 18 digits';
            isValid = false;
        }
    }
    // Email delivery specific validations
    if (formData.invoiceDeliveryMethod === '1') {
        // If useExistingEmail is false (unchecked), secondary email is required
        if (!formData.useExistingEmail) {
            if (!formData.secondaryEmail) {
                errors.secondaryEmail = 'SECONDARY_EMAIL_REQUIRED';
                isValid = false;
            } else if (!validateEmail(formData.secondaryEmail)) {
                errors.secondaryEmail = 'Invalid email address';
                isValid = false;
            }
        }
    }

    // Postal delivery specific validations
    if (formData.invoiceDeliveryMethod === '3') {
        // If useExistingAddress is false (unchecked), postal address fields are required
        if (!formData.useExistingAddress) {
            if (!formData.postAddress) {
                errors.postAddress = 'Postal address is required';
                isValid = false;
            }
            if (!formData.postCity) {
                errors.postCity = 'Postal city is required';
                isValid = false;
            }
            if (!formData.postZipCode) {
                errors.postZipCode = 'Postal zip code is required';
                isValid = false;
            }
            if (!formData.postCountry) {
                errors.postCountry = 'Postal country is required';
                isValid = false;
            }
        }
    }

    return { errors, isValid };
};

/**
 * Validates preferences (Step 3)
 */
export const validateStep3 = (formData: CustomerFormData): { errors: ValidationErrors; isValid: boolean } => {
    const errors: ValidationErrors = {};
    let isValid = true;

    if (!formData.paymentTerms) {
        errors.paymentTerms = 'Payment terms are required';
        isValid = false;
    }

    // Additional API validation - ensure all required API fields are present
    if (!formData.contactEmail) {
        errors.contactEmail = 'Email is required for customer creation';
        isValid = false;
    }

    if (!formData.addressLine1) {
        errors.addressLine1 = 'Address is required for customer creation';
        isValid = false;
    }

    // Only require vatCountryCode if isVATRegistered is not 'false'
    if (formData.isVATRegistered !== 'false' && !formData.vatCountryCode) {
        errors.vatCountryCode = 'Country code is required for customer creation';
        isValid = false;
    }

    if (!formData.phoneNumber) {
        errors.phoneNumber = 'Phone number is required for customer creation';
        isValid = false;
    }

    return { errors, isValid };
};

/**
 * Main validation function that validates the current step
 */
export const validateCurrentStep = (
    currentStep: number,
    formData: CustomerFormData,
    validateVatNumberFn: (vatCountryCode: string, vatNumber: string) => boolean
): { errors: ValidationErrors; isValid: boolean } => {
    switch (currentStep) {
        case 1:
            return validateStep1(formData, validateVatNumberFn);
        case 2:
            return validateStep2(formData);
        case 3:
            return validateStep3(formData);
        default:
            return { errors: {}, isValid: true };
    }
};